import { PublicKey, Connection, Transaction, TransactionInstruction, SystemProgram, ComputeBudgetProgram } from '@solana/web3.js';
import { Buffer } from 'buffer';
import * as anchor from '@project-serum/anchor';
import { BN } from '@project-serum/anchor';
import {
  awaitComputationFinalization,
  getCompDefAccOffset,
  getMXEAccAddress,
  getMempoolAccAddress,
  getCompDefAccAddress,
  getExecutingPoolAccAddress,
  getComputationAccAddress,
  getClusterAccAddress,
  getFeePoolAccAddress,
  getClockAccAddress,
  getArciumProgramId,
  deserializeLE
} from '@arcium-hq/client';

export const ARCIUM_MAIN_PROGRAM_ID = getArciumProgramId();

export const VOTING_PROGRAM_ID = new PublicKey("6TxniqSYQXCZjghxftkJH3znM7A7m1HBpwsQdh7vQogD");

export const CLUSTER_OFFSET = 456;

function randomBytes(size: number): Uint8Array {
  const bytes = new Uint8Array(size);
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < size; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return bytes;
}

export class PollClient {
  private connection: Connection;
  private wallet: anchor.Wallet;
  private provider: anchor.AnchorProvider;
  private programId: PublicKey;
  private lastTransactionSignature: string | null = null;
  private lastComputationOffset: BN | null = null;

  constructor(connection: Connection, wallet: anchor.Wallet, programId?: PublicKey) {
    this.connection = connection;
    this.wallet = wallet;
    // Use the voting program ID by default
    this.programId = programId || VOTING_PROGRAM_ID;

    this.provider = new anchor.AnchorProvider(
      connection,
      wallet,
      { commitment: 'confirmed' }
    );
  }

  async getMXEPublicKeyWithRetry(): Promise<Uint8Array> {
    // Use the MXE public key extracted from the deployed MXE account
    const mxePublicKeyHex = '0160e4641b73511174fea527412fdfa1b68e1ddb2c1c55a296c251bc3b574b59';
    return new Uint8Array(Buffer.from(mxePublicKeyHex, 'hex'));
  }

  async initializeVoteSystem(): Promise<boolean> {
    try {
      // First ensure MXE is initialized
      await this.ensureMXEInitialized();

      // Then check and initialize computation definitions
      await this.ensureComputationDefinitionsInitialized();

      return true;
    } catch (error) {
      console.error("Error initializing vote system:", error);
      return false;
    }
  }

  async ensureMXEInitialized(): Promise<void> {
    const mxeAccount = getMXEAccAddress(this.programId);
    try {
      const accountInfo = await this.connection.getAccountInfo(mxeAccount);
      if (!accountInfo) {
        await this.initMXEAccount();
      } else {
        
      }
    } catch (error) {
      await this.initMXEAccount();
    }
  }

  async initMXEAccount(): Promise<void> {
    const initMXEDiscriminator = Buffer.from([240, 227, 11, 166, 193, 167, 25, 79]);
    const mxeAccount = getMXEAccAddress(this.programId);
    const feePoolAccount = getFeePoolAccAddress();
    const clockAccount = getClockAccAddress();

    const keys = [
      { pubkey: this.wallet.publicKey, isSigner: true, isWritable: true },
      { pubkey: mxeAccount, isSigner: false, isWritable: true },
      { pubkey: ARCIUM_MAIN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: clockAccount, isSigner: false, isWritable: true },
      { pubkey: feePoolAccount, isSigner: false, isWritable: true },
    ];

    const transaction = new Transaction();
    const initMXEIx = new TransactionInstruction({
      keys,
      programId: ARCIUM_MAIN_PROGRAM_ID,
      data: initMXEDiscriminator
    });
    transaction.add(initMXEIx);

    const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
      units: 200000
    });
    transaction.add(modifyComputeUnits);

    const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: 1_000_000
    });
    transaction.add(addPriorityFee);

    const { blockhash } = await this.connection.getLatestBlockhash();
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = this.wallet.publicKey;

    const signedTx = await this.wallet.signTransaction(transaction);
    const signature = await this.connection.sendRawTransaction(signedTx.serialize());

    console.log("MXE init transaction:", signature);
    await this.connection.confirmTransaction(signature, 'confirmed');

    console.log("MXE account initialized successfully");
  }

  async ensureComputationDefinitionsInitialized(): Promise<void> {
    const compDefs = ['init_vote_stats', 'vote', 'reveal_result'];

    for (const compDef of compDefs) {
      const compDefOffset = Buffer.from(getCompDefAccOffset(compDef)).readUInt32LE();
      const compDefAddress = getCompDefAccAddress(this.programId, compDefOffset);

      try {
        await this.connection.getAccountInfo(compDefAddress);
      } catch (error) {
        await this.initComputationDefinition(compDef);
      }
    }
  }

  async initComputationDefinition(compDefName: string): Promise<void> {
    const compDefOffset = Buffer.from(getCompDefAccOffset(compDefName)).readUInt32LE();
    const compDefAddress = getCompDefAccAddress(this.programId, compDefOffset);
    const mxeAccount = getMXEAccAddress(this.programId);

    // Discriminator for init_computation_definition
    const initCompDefDiscriminator = Buffer.from([117, 167, 172, 48, 88, 134, 95, 91]);

    const keys = [
      { pubkey: this.wallet.publicKey, isSigner: true, isWritable: true },
      { pubkey: mxeAccount, isSigner: false, isWritable: true },
      { pubkey: compDefAddress, isSigner: false, isWritable: true },
      { pubkey: ARCIUM_MAIN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
    ];

    const transaction = new Transaction();
    const initCompDefIx = new TransactionInstruction({
      keys,
      programId: ARCIUM_MAIN_PROGRAM_ID,
      data: initCompDefDiscriminator
    });
    transaction.add(initCompDefIx);

    const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
      units: 200000
    });
    transaction.add(modifyComputeUnits);

    const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: 1_000_000
    });
    transaction.add(addPriorityFee);

    const { blockhash } = await this.connection.getLatestBlockhash();
    transaction.recentBlockhash = blockhash;
    transaction.feePayer = this.wallet.publicKey;

    const signedTx = await this.wallet.signTransaction(transaction);
    const signature = await this.connection.sendRawTransaction(signedTx.serialize());

    console.log(`${compDefName} init transaction:`, signature);
    await this.connection.confirmTransaction(signature, 'confirmed');

    console.log(`${compDefName} computation definition initialized successfully`);
  }

  async pollAccountExists(pollId: number): Promise<boolean> {
    try {
      const [pollPda] = anchor.web3.PublicKey.findProgramAddressSync(
        [
          Buffer.from("poll"), 
          this.wallet.publicKey.toBuffer(), 
          Buffer.from(new Uint32Array([pollId]).buffer)
        ],
        this.programId
      );
      
      const account = await this.connection.getAccountInfo(pollPda);
      return account !== null;
    } catch (error) {
      console.error("Error checking poll account:", error);
      return false;
    }
  }

  async findAvailablePollId(): Promise<number> {
    let candidateId = Math.floor(Math.random() * 1000000);
    
    for (let i = 0; i < 10; i++) {
      const exists = await this.pollAccountExists(candidateId);
      if (!exists) {
        return candidateId;
      }
      candidateId = Math.floor(Math.random() * 1000000);
    }
    
    return Math.floor(Math.random() * 1000000000) + 1000000;
  }

  async createNewPoll(pollId: number, question: string): Promise<string> {
    const exists = await this.pollAccountExists(pollId);
    if (exists) {
      pollId = await this.findAvailablePollId();
    }
    
    try {
      const offsetBytes = randomBytes(8);
      const computationOffset = new anchor.BN(offsetBytes, undefined, 'le');
      this.lastComputationOffset = computationOffset;
      
      const nonce = randomBytes(16);
      const nonceBN = new anchor.BN(deserializeLE(nonce).toString());
      
      const [pollPda] = anchor.web3.PublicKey.findProgramAddressSync(
        [
          Buffer.from("poll"), 
          this.wallet.publicKey.toBuffer(), 
          Buffer.from(new Uint32Array([pollId]).buffer)
        ],
        this.programId
      );
      
      const mxeAccount = getMXEAccAddress(this.programId);
      const mempoolAccount = getMempoolAccAddress(CLUSTER_OFFSET);
      const executingPool = getExecutingPoolAccAddress(CLUSTER_OFFSET);
      const computationAccount = getComputationAccAddress(CLUSTER_OFFSET, computationOffset);
      const compDefAccount = getCompDefAccAddress(
        this.programId,
        Buffer.from(getCompDefAccOffset('init_vote_stats')).readUInt32LE()
      );
      const clusterAccount = getClusterAccAddress(CLUSTER_OFFSET);
      const feePoolAccount = getFeePoolAccAddress();
      const clockAccount = getClockAccAddress();

      // Get sign PDA account - seeds: ["ArciumSignerAccount"]
      const signPdaAccount = PublicKey.findProgramAddressSync(
        [Buffer.from("ArciumSignerAccount")],
        this.programId
      )[0];

      const createNewPollDiscriminator = Buffer.from([18, 23, 205, 123, 193, 24, 162, 162]);
      
      const data = Buffer.concat([
        createNewPollDiscriminator,
        computationOffset.toArrayLike(Buffer, 'le', 8),
        (() => {
          const idBuffer = Buffer.alloc(4);
          idBuffer.writeUInt32LE(pollId, 0);
          return idBuffer;
        })(),
        (() => {
          const strBytes = Buffer.from(question);
          const lenBuf = Buffer.alloc(4);
          lenBuf.writeUInt32LE(strBytes.length, 0);
          return Buffer.concat([lenBuf, strBytes]);
        })(),
        nonceBN.toArrayLike(Buffer, 'le', 16)
      ]);

      const keys = [
        { pubkey: this.wallet.publicKey, isSigner: true, isWritable: true },
        { pubkey: signPdaAccount, isSigner: false, isWritable: true },
        { pubkey: mxeAccount, isSigner: false, isWritable: true },
        { pubkey: mempoolAccount, isSigner: false, isWritable: true },
        { pubkey: executingPool, isSigner: false, isWritable: true },
        { pubkey: computationAccount, isSigner: false, isWritable: true },
        { pubkey: compDefAccount, isSigner: false, isWritable: false },
        { pubkey: clusterAccount, isSigner: false, isWritable: true },
        { pubkey: feePoolAccount, isSigner: false, isWritable: true },
        { pubkey: clockAccount, isSigner: false, isWritable: true },
        { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
        { pubkey: ARCIUM_MAIN_PROGRAM_ID, isSigner: false, isWritable: false },
        { pubkey: pollPda, isSigner: false, isWritable: true },
      ];

      const transaction = new Transaction();
      
      const createPollIx = new TransactionInstruction({
        keys,
        programId: this.programId,
        data
      });
      transaction.add(createPollIx);
      
      const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({ 
        units: 200000
      });
      transaction.add(modifyComputeUnits);

      const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
        microLamports: 1_000_000
      });
      transaction.add(addPriorityFee);
      
      const { blockhash, lastValidBlockHeight } = await this.connection.getLatestBlockhash('confirmed');
      transaction.recentBlockhash = blockhash;
      transaction.lastValidBlockHeight = lastValidBlockHeight;
      transaction.feePayer = this.wallet.publicKey;
      
      const signedTx = await this.wallet.signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signedTx.serialize());

      this.lastTransactionSignature = signature;

      await this.connection.confirmTransaction(signature, 'confirmed');

      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Finalization timeout')), 3000)
        );

        const finalizationPromise = awaitComputationFinalization(
          this.provider as any,
          computationOffset,
          this.programId,
          'confirmed'
        );

        await Promise.race([
          timeoutPromise,
          finalizationPromise
        ]);

        return signature;
      } catch (error) {
        return signature;
      }
    } catch (error) {
      console.error('Error creating poll:', error);

      // If it's a SendTransactionError, try to get detailed logs
      if (error instanceof Error && 'getLogs' in error) {
        try {
          const logs = (error as any).getLogs();
          console.error("Transaction logs:", logs);
        } catch (logError) {
          console.error("Could not get transaction logs:", logError);
        }
      }

      throw error;
    }
  }

  getLastTransactionSignature(): string | null {
    return this.lastTransactionSignature;
  }
} 